import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import type { Quiz, QuestionResult } from '../types';
import { quizzes } from '../lib/api';
import { cn } from '../utils/cn';

interface QuizPlayerProps {
  quiz: Quiz;
  onComplete: (score: number, results: QuestionResult[]) => void;
}

export function QuizPlayer({ quiz, onComplete }: QuizPlayerProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: string[] }>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize answers object
    const initialAnswers = quiz.questions.reduce((acc, question) => ({
      ...acc,
      [question.id]: []
    }), {});
    setAnswers(initialAnswers);
  }, [quiz]);

  const handleSelectAnswer = (questionId: string, optionId: string) => {
    setAnswers(prev => {
      const question = quiz.questions.find(q => q.id === questionId);
      if (!question) return prev;

      // For single choice questions, replace the answer
      // For multiple choice, toggle the option
      const newAnswers = question.type === 'single'
        ? [optionId]
        : prev[questionId].includes(optionId)
          ? prev[questionId].filter(id => id !== optionId)
          : [...prev[questionId], optionId];

      return {
        ...prev,
        [questionId]: newAnswers
      };
    });

    // Auto advance for single choice questions after a short delay
    const question = quiz.questions[currentQuestion];
    if (question.type === 'single' && currentQuestion < quiz.questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
      }, 500);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate all required questions are answered
      const unansweredRequired = quiz.questions.filter(q => 
        q.required && (!answers[q.id] || answers[q.id].length === 0)
      );

      if (unansweredRequired.length > 0) {
        throw new Error('Por favor, responda todas as questões obrigatórias antes de finalizar.');
      }

      // Submit answers and get results
      const result = await quizzes.submitAnswer(quiz.id, Object.entries(answers).map(([questionId, selectedOptions]) => ({
        questionId,
        selectedOptions
      })));

      // Map results to QuestionResult format
      const questionResults: QuestionResult[] = result.details.map(detail => {
        const question = quiz.questions.find(q => q.id === detail.questionId);
        return {
          questionId: detail.questionId,
          question: question?.text || '',
          userAnswer: question?.options
            .filter(opt => detail.selectedOptions.includes(opt.id))
            .map(opt => opt.text)
            .join(', ') || '',
          correctAnswer: question?.options
            .filter(opt => opt.isCorrect)
            .map(opt => opt.text)
            .join(', ') || '',
          isCorrect: detail.isCorrect,
          explanation: question?.explanation
        };
      });

      onComplete(result.score, questionResults);
    } catch (err: any) {
      console.error('Error submitting quiz:', err);
      setError(err.message || 'Erro ao enviar avaliação. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestionData = quiz.questions[currentQuestion];
  if (!currentQuestionData) return null;

  const isLastQuestion = currentQuestion === quiz.questions.length - 1;
  const hasAnsweredCurrent = answers[currentQuestionData.id]?.length > 0;
  const hasAnsweredAll = quiz.questions.every(q => 
    !q.required || (answers[q.id] && answers[q.id].length > 0)
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">
            Questão {currentQuestion + 1} de {quiz.questions.length}
          </span>
          <div className="h-2 w-32 rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full transition-all duration-300 bg-[#7c3aed]"
              style={{ 
                width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%`
              }}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-8">
        <p className="text-xl font-semibold text-gray-900 mb-6">
          {currentQuestionData.text}
          {currentQuestionData.required && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </p>

        <div className="space-y-4">
          {currentQuestionData.options.map((option) => {
            const isSelected = answers[currentQuestionData.id]?.includes(option.id);
            
            return (
              <button
                key={option.id}
                onClick={() => handleSelectAnswer(currentQuestionData.id, option.id)}
                className={cn(
                  "w-full rounded-lg border-2 p-4 text-left transition-all",
                  "hover:border-purple-200 hover:bg-purple-50 active:scale-[0.99]",
                  isSelected && "border-purple-500 bg-purple-50 text-purple-700"
                )}
              >
                {option.text}
              </button>
            );
          })}
        </div>

        {currentQuestionData.explanation && hasAnsweredCurrent && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              {currentQuestionData.explanation}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
          disabled={currentQuestion === 0}
          className={cn(
            "flex items-center gap-2 rounded-lg px-4 py-2",
            "text-gray-700 border border-gray-300",
            "transition-all hover:bg-gray-50 active:scale-95",
            "disabled:opacity-50 disabled:hover:bg-transparent disabled:active:scale-100"
          )}
        >
          <ArrowLeft className="h-5 w-5" />
          Anterior
        </button>

        {isLastQuestion && hasAnsweredAll ? (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={cn(
              "flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white",
              "transition-all hover:opacity-90 active:scale-95 disabled:opacity-50",
              "disabled:hover:opacity-50 disabled:active:scale-100",
              "bg-[#7c3aed]"
            )}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Enviando...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Finalizar Avaliação
              </>
            )}
          </button>
        ) : (
          <button
            onClick={() => setCurrentQuestion(Math.min(quiz.questions.length - 1, currentQuestion + 1))}
            disabled={!hasAnsweredCurrent || currentQuestion === quiz.questions.length - 1}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2 text-white",
              "transition-all hover:opacity-90 active:scale-95",
              "disabled:opacity-50 disabled:hover:opacity-50 disabled:active:scale-100",
              "bg-[#7c3aed]"
            )}
          >
            Próxima
            <ArrowRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}